import { IFixture } from "core-integration/src/store/fixtures/types";
import { SupportedLanguage } from "../language";
import {
  AllowedNewsType,
  PulseBroadcasterResponseType,
  PulseContentItem,
  PulseContentResponseType,
} from "./types";

export const getContentType = (type: string) => {
  return type === "text" ? "news" : "video";
};

export const isAllowedNewsType = (type: string): type is AllowedNewsType =>
  type === "text" || type === "video";

// Set from .env files
const baseUrl = import.meta.env.VITE_PULSE_API_BASE || "";
const sdpBaseUrl = import.meta.env.VITE_PULSE_SDP_API_BASE || "";

export interface IPulsePlaylistItem {
  id: number;
  type: string;
  response: PulseContentItem;
}

export interface IPulsePlaylist {
  items: IPulsePlaylistItem[];
  title: string;
}

interface PulseTeam {
  metadata: {
    fantasy_shirt_url: string;
  };
}

interface PulsePlayer {
  metadata: {
    fantasy_web_profile_url: string;
    fantasy_buy_player_shirt_url?: string;
  };
}

async function _fetch(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return await response.json();
}

type ContentType = "TEXT" | "VIDEO";
type FilterType = "tagNames" | "tagExpression";

const fetchNewsContent = async (
  contentType: ContentType,
  filterType: FilterType,
  filterValue: string,
  lang: SupportedLanguage,
  limit: number = 10,
  offset: number = 0,
): Promise<PulseContentResponseType> => {
  try {
    const params = new URLSearchParams({
      contentTypes: contentType,
      offset: offset.toString(),
      limit: limit.toString(),
      onlyRestrictedContent: "false",
      detail: "DETAILED",
      [filterType]: filterValue,
    });

    const url = `${baseUrl}/content/premierleague/${lang}?${params.toString()}`;
    return await _fetch(url);
  } catch (error) {
    console.error(
      `Failed to fetch ${contentType} content with ${filterType}: ${filterValue}`,
      error,
    );
    throw error;
  }
};

export const fetchNewsByTagNames = async (
  tagNames: string,
  lang: SupportedLanguage,
  limit: number = 10,
  offset: number = 0,
): Promise<PulseContentResponseType> =>
  fetchNewsContent("TEXT", "tagNames", tagNames, lang, limit, offset);

export const fetchNewsByTagExpression = async (
  tagExpression: string,
  lang: SupportedLanguage,
  contentType: ContentType = "TEXT",
  limit: number = 10,
  offset: number = 0,
): Promise<PulseContentResponseType> =>
  fetchNewsContent(
    contentType,
    "tagExpression",
    tagExpression,
    lang,
    limit,
    offset,
  );

export const fetchNewsArticle = async (
  id: number,
  lang: SupportedLanguage,
): Promise<PulseContentItem> => {
  try {
    const url = `${baseUrl}/content/premierleague/TEXT/${lang}/${id}`;
    return await _fetch(url);
  } catch (error) {
    console.error(`Failed to fetch news article ${id}:`, error);
    throw error;
  }
};

export const fetchPlaylist = async (
  id: number,
  lang: SupportedLanguage,
): Promise<IPulsePlaylist> => {
  try {
    const url = `${baseUrl}/content/PremierLeague/playlist/${lang}/${id}?detail=DETAILED`;
    const response = await _fetch(url);
    return { items: response.items, title: response.title };
  } catch (error) {
    console.error(`Failed to fetch playlist ${id}:`, error);
    throw error;
  }
};

// Broadcasters
export async function fetchBroadcastersForFixtures(
  fixtureCode: string,
): Promise<PulseBroadcasterResponseType> {
  const url = `${baseUrl}/broadcasting/match-events?sportDataId=${fixtureCode}&pageSize=20`;
  return _fetch(url);
}

export const fetchEventTeamNews = async (
  fixtures: IFixture[],
  lang: SupportedLanguage,
  count?: number,
): Promise<PulseContentItem[]> => {
  // Use mock data in test environment
  if (import.meta.env.VITE_USE_MOCK_PULSE_API === "true") {
    const { getMockPulseContentItems } =
      await import("../../../../../plfpl/src/components/ClubTeamNews/__mocks__/pulseApiResponses");
    return getMockPulseContentItems();
  }

  // Real API implementation
  try {
    const referenceExpression = fixtures
      .map((fixture) => `"SDP_FOOTBALL_MATCH:${fixture.code}"`)
      .join("or");

    const params = new URLSearchParams({
      contentTypes: "TEXT",
      offset: "0",
      limit: (count || 20).toString(),
      onlyRestrictedContent: "false",
      tagExpression: `("ClubTeamNews")`,
      referenceExpression,
      detail: "DETAILED",
    });

    const url = `${baseUrl}/content/premierleague/${lang}?${params.toString()}`;
    const { content } = await _fetch(url);
    return content as PulseContentItem[];
  } catch (error) {
    console.error("Failed to fetch team news articles", error);
    throw error;
  }
};

export const fetchTeam = async (teamCode: number): Promise<PulseTeam> => {
  try {
    const url = `${sdpBaseUrl}/v1/metadata/SDP_FOOTBALL_TEAM/${teamCode}`;
    return _fetch(url);
  } catch (error) {
    console.error("Failed to fetch team", error);
    throw error;
  }
};

export const fetchPlayer = async (playerCode: number): Promise<PulsePlayer> => {
  try {
    const url = `${sdpBaseUrl}/v1/metadata/SDP_FOOTBALL_PLAYER/${playerCode}`;
    return _fetch(url);
  } catch (error) {
    console.error("Failed to fetch player", error);
    throw error;
  }
};
